import { AxiosError } from 'axios';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import {
  DropdownWrapperMedium,
  InputWrapperLarge,
  InputWrapperMedium,
} from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Header from '../../../common/Header/Header';
import { TableRowWrapper, TableWrapper } from '../../../common/SideBySideTableLayout/SideBySideTableLayout.styles';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import Checkbox from '../../../components/Checkbox/Checkbox';

import Dropdown from '../../../components/Dropdown/Dropdown';
import { Options } from '../../../components/Dropdown/Dropdown.types';
import Input from '../../../components/Input/Input';
import Radio from '../../../components/Radio/Radio';
import Table, { tableToolTipValueGetter } from '../../../components/Table/Table';
import { showToast } from '../../../components/Toast/Toast';
import { MENU_STATUS, STATUS } from '../../../constants/ApiEnums';
import { useGetMenuItemRecipeBases, useGetMenuItemSubRecipeBases } from '../../../services/base/base.service';
import { Base } from '../../../services/base/base.types';
import { useGetMenuItemRecipeClasses, useGetMenuItemSubRecipeClasses } from '../../../services/class/class.service';
import { Class } from '../../../services/class/class.types';
import {
  useEditMenu,
  useMenu,
  useMenuItemRecipe,
  useGetMenuItemSubRecipes,
  useClearMissingSpecs,
} from '../../../services/menu/menu.service';
import { useGetMenuItemRecipeSizes, useGetMenuItemSubRecipeSizes } from '../../../services/size/size.service';
import { Size } from '../../../services/size/size.types';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import {
  genericErrorShowToastOptions,
  useGenericErrorMessage,
} from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { EditMenuItemSpecModal } from '../EditMenuItemSpecModal/EditMenuItemSpecModal';
import {
  H2WithMargin,
  HR,
  MenuForm,
  RadioWrapper,
  RowWrapper,
  SaveButton,
  SectionLabel,
  SelectionRowWrapper,
  Label,
  ClearContainer,
} from './EditMenu.styles';
import { MenuFormValues } from './EditMenu.types';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { useTheme } from '@emotion/react';
import DeleteConfirmationModal from '../../../common/DeleteConfirmationModal/DeleteConfirmationModal';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { ANY_OPTION } from '../../../constants/Dropdown';
import { useGetMenuItemRecipeStrategies } from "../../../services/strategy/strategy.service";
import { Strategy } from "../../../services/strategy/strategy.types";

const EditMenu: FC = () => {
  const queryClient = useQueryClient();
  const { menuNo } = useParams();
  const _menuNo = menuNo as string;

  const theme = useTheme();
  const { data: menu, isError: menuError, refetch } = useMenu(_menuNo, true);
  const { mutate, isError: editMenuIsError, isLoading, error: editMenuError } = useEditMenu(_menuNo);

  // Filters for Recipes and Menu Items
  const { data: strategies, isError: strategiesError } = useGetMenuItemRecipeStrategies(_menuNo, true);
  const [selectedStrategy, setSelectedStrategy] = useState<string | undefined>(undefined);
  const { data: classes, isError: classesError } =
      useGetMenuItemRecipeClasses(
          _menuNo,
          {
            strategyNo: selectedStrategy !== ANY_OPTION.toString() ? selectedStrategy : undefined
          },
          true
      );
  const [selectedClass, setSelectedClass] = useState<number | undefined>(undefined);
  const { data: bases, isError: basesError } =
      useGetMenuItemRecipeBases(
          _menuNo,
          {
            classNo: selectedClass !== ANY_OPTION ? selectedClass : undefined,
            strategyNo: selectedStrategy !== ANY_OPTION.toString() ? selectedStrategy : undefined
          },
          true
      );
  const [selectedBase, setSelectedBase] = useState<number | undefined>(undefined);
  const { data: sizes, isError: sizesError } =
      useGetMenuItemRecipeSizes(
          _menuNo,
          {
            classNo: selectedClass !== ANY_OPTION ? selectedClass : undefined,
            baseNo: selectedBase !== ANY_OPTION ? selectedBase : undefined,
            strategyNo: selectedStrategy !== ANY_OPTION.toString() ? selectedStrategy : undefined
          },
          true
      );
  const [selectedSize, setSelectedSize] = useState<number | undefined>(undefined);

  const [isChecked, setIsChecked] = useState(false);
  const handleCheck = () => {
    setIsChecked(!isChecked);
  };

  //returns every single menu item if checkbox is clicked
  const allMenuItemRecipes = () => {
    return isChecked
        ? {}
        : {
          classNo: selectedClass !== ANY_OPTION ? selectedClass : undefined,
          baseNo: selectedBase !== ANY_OPTION ? selectedBase : undefined,
          sizeNo: selectedSize !== ANY_OPTION ? selectedSize : undefined,
          strategyNo: selectedStrategy !== ANY_OPTION.toString() ? selectedStrategy : undefined
        };
  };

  const {
    data: menuItemRecipeData,
    isError: menuItemRecipeError,
    refetch: menuItemRecipeRefetch,
  } = useMenuItemRecipe(_menuNo, allMenuItemRecipes(), true);

  //filters specifically for menu item recipes carrying null specs
  const menuRecipeData = menuItemRecipeData?.data.filter((item) => {
    return item.specDesc === null;
  });

  const nullSpecRecipeDataLabel = menuRecipeData?.map((item: any) => {
    return { ...item, specDesc: 'No Spec Match for this Menu Item' };
  });

  // Filters for Subrecipes and Menu Items
  const { data: subrecipesClasses, isError: subrecipesClassesError } = useGetMenuItemSubRecipeClasses(_menuNo, true);
  const [selectedSubrecipeClass, setSelectedSubrecipeClass] = useState<number | undefined>(undefined);
  const { data: subrecipesBases, isError: subrecipesBasesError } = useGetMenuItemSubRecipeBases(
    _menuNo,
    { classNo: selectedSubrecipeClass },
    true,
  );
  const [selectedSubrecipeBase, setSelectedSubrecipeBase] = useState<number | undefined>(undefined);
  const { data: subrecipesSizes, isError: subrecipesSizesError } = useGetMenuItemSubRecipeSizes(
    _menuNo,
    {
      classNo: selectedSubrecipeClass,
      baseNo: selectedSubrecipeBase !== ANY_OPTION ? selectedSubrecipeBase : undefined,
    },
    true,
  );
  const [selectedSubrecipeSize, setSelectedSubrecipeSize] = useState<number | undefined>(undefined);

  //returns every single submenu item if checkbox is clicked
  const allSubMenuItemRecipes = () => {
    return isChecked
      ? {}
      : {
        classNo: selectedSubrecipeClass,
        baseNo: selectedSubrecipeBase !== ANY_OPTION ? selectedSubrecipeBase : undefined,
        sizeNo: selectedSubrecipeSize !== ANY_OPTION ? selectedSubrecipeSize : undefined,
      };
  };
  const {
    data: getMenuItemSubRecipesResponse,
    isError: getMenuItemSubRecipesIsError,
    refetch: getMenuItemSubRecipesRefetch,
  } = useGetMenuItemSubRecipes(_menuNo, allSubMenuItemRecipes());

  //filters specifically for submenu item recipes carrying null specs
  const menuSubRecipeData = getMenuItemSubRecipesResponse?.data.filter((item) => {
    return item.specDesc === null;
  });
  const nullSpecSubRecipeDataLabel = menuSubRecipeData?.map((item: any) => {
    return { ...item, specDesc: 'No Spec Match for this Menu Item' };
  });

  //grid renders only null specs for both menu/submenu items if isChecked is true
  const menuItemRecipes = isChecked ? nullSpecRecipeDataLabel : menuItemRecipeData?.data || [];
  const menuItemSubrecipes = isChecked ? nullSpecSubRecipeDataLabel : getMenuItemSubRecipesResponse?.data || [];

  // Process data to create combined NoDesc fields
  menuItemRecipes?.forEach((element: any) => {
    element.classNoDesc = element.classNo + ' - ' + element.classDesc;
    element.baseNoDesc = element.baseNo + ' - ' + element.baseDesc;
    element.sizeNoDesc = element.sizeNo + ' - ' + element.sizeDesc;
    element.prodNoDesc = element.prodNo + ' - ' + element.prodDesc;
    
    // Handle time frame fields
    element.dayNoDesc = element.dayNo == null ? ' -- ' : element.dayNo + ' - ' + element.dayDesc;
    element.timeNoDesc = element.timeNo == null ? ' -- ' : element.timeNo + ' - ' + element.timeDesc;
    element.dateRangeNoDesc = element.dateRangeNo == null ? ' -- ' : element.dateRangeNo + ' - ' + element.dateRangeDesc;
    element.occasionNoDesc = element.occasionNo == null ? ' -- ' : element.occasionNo + ' - ' + element.occasionDesc;
  });

  menuItemSubrecipes?.forEach((element: any) => {
    element.classNoDesc = element.classNo + ' - ' + element.classDesc;
    element.baseNoDesc = element.baseNo + ' - ' + element.baseDesc;
    element.sizeNoDesc = element.sizeNo + ' - ' + element.sizeDesc;
    element.prodNoDesc = element.prodNo + ' - ' + element.prodDesc;
    element.optionNoDesc = element.optionNo + ' - ' + element.optionDesc;
    
    // Handle time frame fields
    element.dayNoDesc = element.dayNo == null ? ' -- ' : element.dayNo + ' - ' + element.dayDesc;
    element.timeNoDesc = element.timeNo == null ? ' -- ' : element.timeNo + ' - ' + element.timeDesc;
    element.dateRangeNoDesc = element.dateRangeNo == null ? ' -- ' : element.dateRangeNo + ' - ' + element.dateRangeDesc;
    element.occasionNoDesc = element.occasionNo == null ? ' -- ' : element.occasionNo + ' - ' + element.occasionDesc;
  });

  const notPending = menu?.data?.statusNo !== MENU_STATUS.PENDING;

  const [incompleteFlag, setIncompleteFlag] = useState(false);
  const [hasMissingSpecs, setHasMissingSpecs] = useState(false);
  const [clearConfirmationModalVisible, setClearConfirmationModalVisible] = useState(false);

  useEffect(() => {
    if (classes?.data) {
      setSelectedClass(classes?.data?.[0]?.classNo);
    }
  }, [classes]);

  useEffect(() => {
    if (bases?.data) {
      setSelectedBase(bases?.data?.[0]?.baseNo);
    }
  }, [bases]);

  useEffect(() => {
    if (sizes?.data) {
      setSelectedSize(sizes?.data?.[0]?.sizeNo);
    }
  }, [sizes]);

  useEffect(() => {
    if (subrecipesClasses?.data) {
      setSelectedSubrecipeClass(subrecipesClasses?.data?.[0]?.classNo);
    }
  }, [subrecipesClasses]);

  useEffect(() => {
    if (subrecipesBases?.data) {
      setSelectedSubrecipeBase(subrecipesBases?.data?.[0]?.baseNo);
    }
  }, [subrecipesBases]);

  useEffect(() => {
    if (subrecipesSizes?.data) {
      setSelectedSubrecipeSize(subrecipesSizes?.data?.[0]?.sizeNo);
    }
  }, [subrecipesSizes]);

  useEffect(() => {
    setHasMissingSpecs(incompleteFlag && isChecked);
  }, [incompleteFlag, isChecked]);

  const [formValues, setFormValues] = useState<MenuFormValues>({
    description: '',
    effectiveDate: '',
  });

  const [initialFormValues, setInitialFormValues] = useState<MenuFormValues>({
    description: '',
    effectiveDate: '',
  });

  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isRecipes, setIsRecipes] = useState(true);

  const checkForUnsavedChanges = () => {
    if (
      formValues.description !== initialFormValues.description ||
      formValues.effectiveDate !== initialFormValues.effectiveDate
    ) {
      setUnsavedChanges(true);
      return;
    }
    setUnsavedChanges(false);
  };

  useEffect(() => {
    checkForUnsavedChanges();
  }, [menu?.data, formValues]);

  useEffect(() => {
    if (menu?.data) {
      setFormValues({
        description: menu.data?.description,
        effectiveDate: menu.data?.effectiveDate?.slice(0, 10),
      });
      setInitialFormValues({
        description: menu.data?.description,
        effectiveDate: menu.data?.effectiveDate?.slice(0, 10),
      });
      setIncompleteFlag(menu.data?.incompFlag);
    }
  }, [menu?.data]);

  useFetchErrorToast([
    { isError: menuError, label: 'Error fetching menu', id: 'menuFetchError' },
    { isError: strategiesError, label: 'Error fetching strategies', id: 'strategyFetchError' },
    { isError: classesError, label: 'Error fetching classes', id: 'classFetchError' },
    { isError: basesError, label: 'Error fetching bases', id: 'baseFetchError' },
    { isError: sizesError, label: 'Error fetching sizes', id: 'sizeFetchError' },
    { isError: subrecipesClassesError, label: 'Error fetching subrecipe classes', id: 'subrecipeClassFetchError' },
    { isError: subrecipesBasesError, label: 'Error fetching subrecipe bases', id: 'subrecipeBaseFetchError' },
    { isError: subrecipesSizesError, label: 'Error fetching subrecipe sizes', id: 'subrecipeSizeFetchError' },
    { isError: menuItemRecipeError, label: 'Error fetching menu items in recipes', id: 'menuItemRecipeFetchError' },
    {
      isError: getMenuItemSubRecipesIsError,
      label: 'Error fetching menu items in subrecipes',
      id: 'menuItemSubrecipeFetchError',
    },
  ]);

  useGenericErrorMessage([{ isError: editMenuIsError, error: editMenuError }]);

  // Dropdown options for Recipes
  const strategyRecipesOptions = useMemo(() => {
    if (strategies?.data) {
      let strategyOptions: Options[] = strategies?.data?.map((value: Strategy) => {
        return { value: value.strategyNo?.toString(), label: value.strategyNo?.toString() + ' - ' + value.description };
      });
      strategyOptions.push({ value: ANY_OPTION.toString(), label: '999 - All Strategies' });
      return strategyOptions;
    } else {
      return [];
    }
  }, [strategies]);

  const classRecipesOptions: Options[] = useMemo(() => {
    if (classes?.data) {
      let classOptions: Options[] = classes?.data?.map((value: Class) => {
        return { value: value.classNo?.toString(), label: value.classNo?.toString() + ' - ' + value.description };
      });
      classOptions.push({ value: ANY_OPTION.toString(), label: '999 - All Classes' });
      return classOptions;
    } else {
      return [];
    }
  }, [classes]);

  const baseRecipesOptions: Options[] = useMemo(() => {
    if (bases?.data) {
      let basesOptions: Options[] = bases?.data?.map((base: Base) => {
        return { value: base.baseNo?.toString(), label: base.baseNo?.toString() + ' - ' + base.description };
      });
      basesOptions.push({ value: ANY_OPTION.toString(), label: '999 - All Bases' });
      return basesOptions;
    } else {
      return [];
    }
  }, [bases]);

  const sizeRecipesOptions: Options[] = useMemo(() => {
    if (sizes?.data) {
      let sizeOptions: Options[] = sizes?.data?.map((size: Size) => {
        return { value: size.sizeNo?.toString(), label: size.sizeNo?.toString() + ' - ' + size.description };
      });
      sizeOptions.push({ value: ANY_OPTION.toString(), label: '999 - All Sizes' });
      return sizeOptions;
    } else {
      return [];
    }
  }, [sizes]);

  // Dropdown options for Subrecipes
  const classSubrecipesOptions = useMemo(() => {
    if (subrecipesClasses?.data) {
      return subrecipesClasses?.data?.map((value: Class) => {
        return { value: value.classNo?.toString(), label: value.classNo?.toString() + ' - ' + value.description };
      });
    } else {
      return [];
    }
  }, [subrecipesClasses]);

  const baseSubrecipesOptions: Options[] = useMemo(() => {
    if (subrecipesBases?.data) {
      let subRecipesBasesOptions: Options[] = subrecipesBases?.data?.map((base: Base) => {
        return { value: base.baseNo?.toString(), label: base.baseNo?.toString() + ' - ' + base.description };
      });
      subRecipesBasesOptions.push({ value: ANY_OPTION.toString(), label: '999 - All Bases' });
      return subRecipesBasesOptions;
    } else {
      return [];
    }
  }, [subrecipesBases]);

  const sizeSubrecipesOptions: Options[] = useMemo(() => {
    if (subrecipesSizes?.data) {
      let subRecipesSizesOptions: Options[] = subrecipesSizes?.data?.map((size: Size) => {
        return { value: size.sizeNo?.toString(), label: size.sizeNo?.toString() + ' - ' + size.description };
      });
      subRecipesSizesOptions.push({ value: ANY_OPTION.toString(), label: '999 - All Sizes' });
      return subRecipesSizesOptions;
    } else {
      return [];
    }
  }, [subrecipesSizes]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleRadioChange = () => {
    setIsRecipes(!isRecipes);
  };

  const handleStrategySelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedStrategy(event.target.value);
  };

  const handleClassSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    isRecipes ? setSelectedClass(+event.target.value) : setSelectedSubrecipeClass(+event.target.value);
  };

  const handleBaseSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    isRecipes ? setSelectedBase(+event.target.value) : setSelectedSubrecipeBase(+event.target.value);
  };

  const handleSizeSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    isRecipes ? setSelectedSize(+event.target.value) : setSelectedSubrecipeSize(+event.target.value);
  };

  const closeClearConfirmationModal = () => {
    setClearConfirmationModalVisible(false);
  };

  const handleClearClick = () => {
    setClearConfirmationModalVisible(true);
  };

  const { mutate: clearMissingSpecs } = useClearMissingSpecs();

  const handleClearMissingSpecs = () => {
    clearMissingSpecs(menuNo || '', {
      onSuccess: () => {
        closeClearConfirmationModal();

        // refresh menu
        refetch();

        // refresh recipes
        menuItemRecipeRefetch();
        getMenuItemSubRecipesRefetch();

        showToast({
          id: 'clearMissingSpecsSuccess',
          label: 'Missing specs have been cleared.',
          toastType: ToastTypes.Success,
        });
      },
      onError: () => {
        showToast(genericErrorShowToastOptions);
        closeClearConfirmationModal();
      },
    });
  };

  const handleSave = () => {
    mutate(
      {
        description: formValues.description,
        effectiveDate: formValues.effectiveDate,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([['menus', _menuNo]]);
          setInitialFormValues({ ...formValues });
          showToast({
            label: 'Menu has been updated successfully.',
            id: 'menuUpdateSuccess',
          });
          setUnsavedChanges(false);
          refetch();
        },
        onError: (error) => {
          queryClient.invalidateQueries([['menus', _menuNo]]);
          if ((error as AxiosError).message === 'apiGatewayTimeout') return;
          showToast(genericErrorShowToastOptions);
        },
      },
    );
  };

  return (
    <>
      <Header breadcrumb={{ label: 'Back To All Menus', to: '/menus' }} title={`Menu ${_menuNo}`} />
      <MenuForm>
        <H2WithMargin>Menu Info</H2WithMargin>
        <RowWrapper>
          <InputWrapperMedium>
            <Input label="Menu Number" id="menuNo" value={menu?.data?.menuNo?.toString()} disabled />
          </InputWrapperMedium>
          <InputWrapperMedium>
            <Input label="Store" id="storeNo" value={menu?.data?.storeNo} disabled />
          </InputWrapperMedium>
          <InputWrapperMedium>
            <Input
              label="Status"
              id="statusNo"
              value={
                menu?.data?.statusNo === MENU_STATUS.ACTIVE ? STATUS[MENU_STATUS.ACTIVE] : STATUS[MENU_STATUS.PENDING]
              }
              disabled
            />
          </InputWrapperMedium>
        </RowWrapper>
        <RowWrapper>
          <InputWrapperLarge>
            <Input
              label="Description"
              id="description"
              value={formValues.description}
              onChange={handleChange}
              disabled={notPending}
            />
          </InputWrapperLarge>
          <InputWrapperMedium>
            <Input
              label="Effective Date"
              id="effectiveDate"
              type="date"
              value={formValues.effectiveDate}
              onChange={handleChange}
              disabled={notPending}
            />
          </InputWrapperMedium>
          <InputWrapperMedium>
            <Input
              label="Activate Date"
              id="activatedDate"
              value={
                menu?.data?.activatedDate
                  ? new Date(menu?.data?.activatedDate).toLocaleDateString() +
                  ' ' +
                  new Date(menu?.data?.activatedDate).toLocaleTimeString()
                  : ''
              }
              disabled
            />
          </InputWrapperMedium>
          <InputWrapperMedium>
            <Input label="Updated By" id="updatedBy" value={menu?.data?.updatedBy} disabled />
          </InputWrapperMedium>
          <InputWrapperMedium>
            <Input
              label="Last Update"
              id="updatedDate"
              value={
                menu?.data?.updatedDate
                  ? new Date(menu?.data?.updatedDate).toLocaleDateString() +
                  ' ' +
                  new Date(menu?.data?.updatedDate).toLocaleTimeString()
                  : ''
              }
              disabled
            />
          </InputWrapperMedium>
        </RowWrapper>
        <HR />

        <RowWrapper>
          <SelectionRowWrapper>
            <SectionLabel id="recipeType">Recipe Type</SectionLabel>
            <RadioWrapper role="radiogroup">
              <Radio
                label="Recipes"
                id="recipes"
                checked={isRecipes}
                onChange={handleRadioChange}
                aria-labelledby="recipeType"
              />
              <Radio
                label="Option Recipes"
                id="optionRecipes"
                checked={!isRecipes}
                onChange={handleRadioChange}
                data-testid="OptionRecipesRadio"
                aria-labelledby="recipeType"
              />
            </RadioWrapper>
          </SelectionRowWrapper>

          <SelectionRowWrapper>
            <SectionLabel id="recipeType">Missing Specs</SectionLabel>
            <RowWrapper>
              <Checkbox
                label="Only Show Missing Specs"
                id="missingSpecs"
                onChange={handleCheck}
                checked={isChecked}
                testId="showMissingSpecsCheck"
              />
              {hasMissingSpecs && (
                <ClearContainer onClick={handleClearClick} data-testid="clearMissingSpecsBtn">
                  <DeleteForeverOutlinedIcon htmlColor={theme.colors.blue} />
                  <Label>clear all</Label>
                </ClearContainer>
              )}
            </RowWrapper>
          </SelectionRowWrapper>
        </RowWrapper>

        <SectionLabel>Filters</SectionLabel>
        <RowWrapper>
          {isRecipes &&
              <DropdownWrapperMedium>
                <Dropdown
                    label="Strategy"
                    id="moaStrategyNo"
                    options={strategyRecipesOptions}
                    value={selectedStrategy}
                    onChange={handleStrategySelect}
                    disabled={isChecked}
                    dataTestId={'StrategyDropdown'}
                />
              </DropdownWrapperMedium>
          }
          <DropdownWrapperMedium>
            <Dropdown
              label="Class"
              id="moaClassNo"
              options={isRecipes ? classRecipesOptions : classSubrecipesOptions}
              value={isRecipes ? selectedClass?.toString() : selectedSubrecipeClass?.toString()}
              onChange={handleClassSelect}
              disabled={isChecked}
              dataTestId={'ClassDropdown'}
            />
          </DropdownWrapperMedium>
          <DropdownWrapperMedium>
            <Dropdown
              label="Base"
              id="moaBaseNo"
              options={isRecipes ? baseRecipesOptions : baseSubrecipesOptions}
              value={isRecipes ? selectedBase?.toString() : selectedSubrecipeBase?.toString()}
              onChange={handleBaseSelect}
              disabled={isChecked}
              dataTestId={'BaseDropdown'}
            />
          </DropdownWrapperMedium>
          <DropdownWrapperMedium>
            <Dropdown
              label="Size"
              id="moaSizeNo"
              options={isRecipes ? sizeRecipesOptions : sizeSubrecipesOptions}
              value={isRecipes ? selectedSize?.toString() : selectedSubrecipeSize?.toString()}
              onChange={handleSizeSelect}
              disabled={isChecked}
              dataTestId={'SizeDropdown'}
            />
          </DropdownWrapperMedium>
        </RowWrapper>
        <TableRowWrapper>
          <TableWrapper>
            <Table
              headerTitle="Menu Items"
              height={'650px'}
              width="100%"
              showHeader={true}
              rowData={isRecipes ? menuItemRecipes : menuItemSubrecipes}
              isSizeColumnsToFit={false}
              returnedRecords={isRecipes ? menuItemRecipes?.length || 0 : menuItemSubrecipes?.length || 0}
              tooltipShowDelay={0}
              columnDefs={
                isRecipes
                  ? [
                    {
                      minWidth: 48,
                      field: 'classNoDesc',
                      headerName: 'Class',
                      flex: 0.8,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'baseNoDesc',
                      headerName: 'Base',
                      flex: 0.8,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'sizeNoDesc',
                      headerName: 'Size',
                      flex: 0.8,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'prodNoDesc',
                      headerName: 'Product',
                      flex: 0.8,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'specDesc',
                      headerName: 'Spec',
                      flex: 2,
                      floatingFilter: true,
                      filter: 'agTextColumnFilter',
                      filterParams: {
                        defaultOption: 'contains',
                        suppressAndOrCondition: true,
                      },
                      filterValueGetter: (params: any) => {
                        const specNo = params.data?.specNo;
                        const specDesc = params.data?.specDesc;
                        // For filtering, use the combined format that will be displayed
                        if (specDesc && specDesc.startsWith(`${specNo} - `)) {
                          return specDesc;
                        }
                        return `${specNo} - ${specDesc}`;
                      },
                      cellRenderer: EditMenuItemSpecModal,
                      cellStyle: isChecked ? { color: '#E21216' } : { color: 'black' },
                      cellRendererParams: {
                        menuNo,
                        menuItemRecipes,
                        menuItemSubrecipes,
                        menuItemRecipeRefetch,
                        menuItemSubrecipeRefetch: getMenuItemSubRecipesRefetch,
                        isRecipes,
                        notPending,
                        isChecked,
                      },
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'strategyNo',
                      headerName: 'Strategy',
                      flex: 0.6,
                      floatingFilter: true,
                      resizable: false,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'price',
                      headerName: 'Price',
                      flex: 0.6,
                      floatingFilter: true,
                      resizable: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'dayNoDesc',
                      headerName: 'Days',
                      flex: 0.6,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'timeNoDesc',
                      headerName: 'Time',
                      flex: 0.6,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'dateRangeNoDesc',
                      headerName: 'Date Range',
                      flex: 0.6,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'occasionNoDesc',
                      headerName: 'Occasion',
                      flex: 0.6,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                  ]
                  : [
                    {
                      minWidth: 48,
                      field: 'classNoDesc',
                      headerName: 'Class',
                      flex: 0.8,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'baseNoDesc',
                      headerName: 'Base',
                      flex: 0.8,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'sizeNoDesc',
                      headerName: 'Size',
                      flex: 0.8,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'prodNoDesc',
                      headerName: 'Product',
                      flex: 0.8,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'optionNoDesc',
                      headerName: 'Option',
                      flex: 0.8,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'specDesc',
                      headerName: 'Spec',
                      flex: 2,
                      floatingFilter: true,
                      filter: 'agTextColumnFilter',
                      filterParams: {
                        defaultOption: 'contains',
                        suppressAndOrCondition: true,
                      },
                      filterValueGetter: (params: any) => {
                        const specNo = params.data?.specNo;
                        const specDesc = params.data?.specDesc;
                        // For filtering, use the combined format that will be displayed
                        if (specDesc && specDesc.startsWith(`${specNo} - `)) {
                          return specDesc;
                        }
                        return `${specNo} - ${specDesc}`;
                      },
                      cellRenderer: EditMenuItemSpecModal,
                      cellStyle: isChecked ? { color: '#E21216' } : { color: 'black' },
                      cellRendererParams: {
                        menuNo,
                        menuItemRecipes,
                        menuItemSubrecipes,
                        menuItemRecipeRefetch,
                        menuItemSubrecipeRefetch: getMenuItemSubRecipesRefetch,
                        isRecipes,
                        notPending,
                        isChecked,
                      },
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'strategyNo',
                      headerName: 'Strategy',
                      flex: 0.6,
                      floatingFilter: true,
                      resizable: false,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'price',
                      headerName: 'Price',
                      flex: 0.6,
                      floatingFilter: true,
                      resizable: false,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'dayNoDesc',
                      headerName: 'Days',
                      flex: 0.6,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'timeNoDesc',
                      headerName: 'Time',
                      flex: 0.6,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'dateRangeNoDesc',
                      headerName: 'Date Range',
                      flex: 0.6,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                    {
                      minWidth: 48,
                      field: 'occasionNoDesc',
                      headerName: 'Occasion',
                      flex: 0.6,
                      floatingFilter: true,
                      tooltipValueGetter: tableToolTipValueGetter,
                    },
                  ]
              }
            />
          </TableWrapper>
        </TableRowWrapper>
        {!notPending ? (
          <SaveButton
            testId="saveMenuButton"
            label="Save"
            onClick={handleSave}
            disabled={!unsavedChanges}
            isLoading={isLoading}
          />
        ) : (
          <></>
        )}
      </MenuForm>
      <UnsavedChangesModal unsavedChangesExist={unsavedChanges} />
      <DeleteConfirmationModal
        open={clearConfirmationModalVisible}
        onClose={closeClearConfirmationModal}
        onDelete={handleClearMissingSpecs}
        title="Are you sure you want to clear all missing specs"
        confirmBtnTestId="confirmClearMissingSpecsModalBtn"
        cancelBtnTestId="cancelClearMissingSpecsModalBtn"
        label="Clear All"
      />
    </>
  );
};

export default EditMenu;
